import { useState, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const scrollTo = (id: string) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const handleCategoryClick = (url: string) => {
    setMobileOpen(false);
    window.open(url, '_blank');
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center gap-2 cursor-pointer group"
          >
            <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center">
              <i className="ri-shopping-bag-3-line text-background-50 text-lg"></i>
            </div>
            <span className="font-heading text-lg font-semibold text-foreground-900 whitespace-nowrap">
              Superbuy Sheet
            </span>
          </a>

          <div className="hidden md:flex items-center gap-8">
            <button
              onClick={() => scrollTo('categories')}
              className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
            >
              Categories
            </button>
            <button
              onClick={() => scrollTo('guides')}
              className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
            >
              Guides
            </button>
            <button
              onClick={() => scrollTo('reviews')}
              className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
            >
              Reviews
            </button>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-semibold bg-foreground-900 text-background-50 px-5 py-2 rounded-full hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            <div className="w-5 h-4 relative flex flex-col justify-between">
              <span
                className={`block h-0.5 w-full bg-foreground-900 rounded-full transition-all duration-300 origin-center ${
                  mobileOpen ? 'rotate-45 translate-y-[7px]' : ''
                }`}
              ></span>
              <span
                className={`block h-0.5 w-full bg-foreground-900 rounded-full transition-all duration-300 ${
                  mobileOpen ? 'opacity-0 scale-x-0' : ''
                }`}
              ></span>
              <span
                className={`block h-0.5 w-full bg-foreground-900 rounded-full transition-all duration-300 origin-center ${
                  mobileOpen ? '-rotate-45 -translate-y-[7px]' : ''
                }`}
              ></span>
            </div>
          </button>
        </div>
      </nav>

      <div
        className={`fixed inset-0 z-40 md:hidden transition-all duration-300 ${
          mobileOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
      >
        <div
          className="absolute inset-0 bg-foreground-900/50 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        ></div>

        <div
          className={`absolute top-0 right-0 w-full max-w-sm h-full bg-background-50 shadow-lg transition-transform duration-300 ease-out ${
            mobileOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between h-16 px-4 border-b border-background-200/60">
            <span className="font-heading text-base font-semibold text-foreground-900">Menu</span>
            <button
              onClick={() => setMobileOpen(false)}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-foreground-600 text-xl"></i>
            </button>
          </div>

          <div className="overflow-y-auto h-[calc(100vh-64px)] px-4 py-6">
            <div className="mb-6">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-foreground-400 mb-3 block">
                Navigation
              </span>
              <div className="space-y-1">
                <button
                  onClick={() => scrollTo('categories')}
                  className="flex items-center gap-3 w-full text-left px-4 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-grid-line text-primary-500 text-base"></i>
                  Categories
                </button>
                <button
                  onClick={() => scrollTo('guides')}
                  className="flex items-center gap-3 w-full text-left px-4 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-book-open-line text-accent-500 text-base"></i>
                  Guides
                </button>
                <button
                  onClick={() => scrollTo('reviews')}
                  className="flex items-center gap-3 w-full text-left px-4 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-star-line text-secondary-500 text-base"></i>
                  Reviews
                </button>
              </div>
            </div>

            <div className="mb-6">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-foreground-400 mb-3 block">
                Shop by Category
              </span>
              <div className="space-y-1">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleCategoryClick(cat.url)}
                    className="flex items-center gap-3 w-full text-left px-4 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer group"
                  >
                    <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-background-200/70 group-hover:bg-primary-50 transition-colors">
                      <i className={`${cat.icon} text-foreground-500 group-hover:text-primary-500 text-sm transition-colors`}></i>
                    </span>
                    {cat.name}
                    <i className="ri-arrow-right-up-line text-foreground-300 text-xs ml-auto group-hover:text-primary-400 transition-colors"></i>
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t border-background-200/60 space-y-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 w-full bg-foreground-900 text-background-50 px-6 py-3.5 rounded-full font-semibold text-sm hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                Superbuy Sheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 w-full border-2 border-foreground-900 text-foreground-900 px-6 py-3.5 rounded-full font-semibold text-sm hover:bg-foreground-900 hover:text-background-50 transition-all cursor-pointer whitespace-nowrap"
              >
                Buy It Now
                <i className="ri-shopping-bag-3-line"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}